import { useState, useEffect, useCallback, useRef } from 'react';
import { api, ApiError } from './api';
import { useApp } from './store';
import type { Server, Channel, ChannelCategory, Member, Message, VoiceParticipant, DmThread, Reaction, ServerEmoji, FriendsList } from './types';
import { wsClient, type WSEvent } from './ws';
import { isMention } from './mentions';

// =============================================================================
// useWSEvent — subscribe to a single WS event type cleanly
// =============================================================================
export function useWSEvent<K extends WSEvent['type']>(
  type: K,
  handler: (event: Extract<WSEvent, { type: K }>) => void
): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    return wsClient.on((event) => {
      if (event.type === type) {
        handlerRef.current(event as Extract<WSEvent, { type: K }>);
      }
    });
  }, [type]);
}

// =============================================================================
// useServers — fetch + react to creates/joins
// =============================================================================
export function useServers() {
  const [servers, setServers] = useState<Server[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const list = await api.listServers();
      setServers(list);
      return list;
    } catch (err) {
      console.error('listServers failed', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // If someone joined a server and it's us, refresh — we just got added
  useWSEvent('member.joined', (event) => {
    // We don't know our user ID here; AppShell handles its own presence.
    // Just refresh: cheap and correct.
    if (servers.some((s) => s.id === event.serverId)) {
      // Already in this server — no need to refresh server list, just members
      // (handled by useMembers).
      return;
    }
    // Could be a server we just joined — refresh
    refresh();
  });

  // If our own role changed in any server, the role field in our servers
  // list is stale — refresh so permission-gated UI updates correctly.
  useWSEvent('member.role_changed', () => {
    // Cheap to just always refresh; the hook caller (AppShell) cares about
    // the current server's role specifically. Filtering here would need our
    // user ID which we don't have at this scope.
    refresh();
  });

  // Channel deletion affects the per-server textChannelIds aggregation we
  // use for server-rail unread dots. Refresh on delete keeps that accurate.
  // (Channel creation isn't broadcast — local creators handle their own
  // state, and others only learn about new channels on next list-channels
  // fetch, which happens when they open the server.)
  useWSEvent('channel.deleted', () => {
    refresh();
  });

  // Optimistically apply a drag-reordered rail (top-to-bottom server ids),
  // then persist. On failure, refetch to snap back to the server's truth.
  const reorder = useCallback(
    async (orderedIds: string[]) => {
      let prev: Server[] = [];
      setServers((curr) => {
        prev = curr;
        const byId = new Map(curr.map((s) => [s.id, s]));
        const next = orderedIds.map((id) => byId.get(id)).filter((s): s is Server => !!s);
        // Keep any servers not present in orderedIds (defensive) appended.
        for (const s of curr) if (!orderedIds.includes(s.id)) next.push(s);
        return next;
      });
      try {
        await api.reorderServers(orderedIds);
      } catch (err) {
        console.error('reorderServers failed', err);
        setServers(prev);
      }
    },
    []
  );

  return { servers, loading, refresh, reorder };
}

// =============================================================================
// useChannels — fetch + react to channel changes
// =============================================================================
export function useChannels(serverId: string | null) {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [categories, setCategories] = useState<ChannelCategory[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!serverId) {
      setChannels([]);
      setCategories([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([api.listChannels(serverId), api.listCategories(serverId)])
      .then(([c, cats]) => {
        if (cancelled) return;
        setChannels(c);
        setCategories(cats);
      })
      .catch((err) => console.error('listChannels/Categories failed', err))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [serverId]);

  // React to live channel deletion from other users
  useWSEvent('channel.deleted', (event) => {
    if (event.serverId !== serverId) return;
    setChannels((prev) => prev.filter((c) => c.id !== event.channelId));
  });

  // A channel moved category and/or got reordered within one. Patch the
  // affected channels' categoryId + position; leave the rest alone.
  useWSEvent('channel.moved', (event) => {
    if (event.serverId !== serverId) return;
    setChannels((prev) => {
      const orderMap = new Map<string, number>();
      event.channelIds.forEach((id, i) => orderMap.set(id, (i + 1) * 10));
      const next = prev.map((c) =>
        orderMap.has(c.id)
          ? { ...c, categoryId: event.categoryId, position: orderMap.get(c.id)! }
          : c
      );
      next.sort((a, b) => a.position - b.position);
      return next;
    });
  });

  useWSEvent('category.created', (event) => {
    if (event.serverId !== serverId) return;
    setCategories((prev) =>
      prev.some((c) => c.id === event.category.id) ? prev : [...prev, event.category].sort((a, b) => a.position - b.position)
    );
  });
  useWSEvent('category.updated', (event) => {
    if (event.serverId !== serverId) return;
    setCategories((prev) => prev.map((c) => (c.id === event.category.id ? event.category : c)));
  });
  useWSEvent('category.deleted', (event) => {
    if (event.serverId !== serverId) return;
    setCategories((prev) => prev.filter((c) => c.id !== event.categoryId));
    // Channels in the deleted category fall back to uncategorized.
    setChannels((prev) => prev.map((c) => (c.categoryId === event.categoryId ? { ...c, categoryId: null } : c)));
  });
  useWSEvent('category.reordered', (event) => {
    if (event.serverId !== serverId) return;
    setCategories((prev) => {
      const orderMap = new Map<string, number>();
      event.categoryIds.forEach((id, i) => orderMap.set(id, (i + 1) * 10));
      return prev
        .map((c) => (orderMap.has(c.id) ? { ...c, position: orderMap.get(c.id)! } : c))
        .sort((a, b) => a.position - b.position);
    });
  });

  // React to a reorder broadcast — re-sort our local list to match the new
  // canonical positions. We also patch each channel's `position` field so
  // any UI relying on it stays in sync (and a refetch after this would
  // produce the same order).
  useWSEvent('channel.reordered', (event) => {
    if (event.serverId !== serverId) return;
    setChannels((prev) => {
      const orderMap = new Map<string, number>();
      event.channelIds.forEach((id, i) => orderMap.set(id, (i + 1) * 10));
      // Apply new positions to channels of the affected type; leave others alone.
      const next = prev.map((c) =>
        c.type === event.channelType && orderMap.has(c.id)
          ? { ...c, position: orderMap.get(c.id)! }
          : c
      );
      // Stable sort by (type, position). Group order (text-then-voice) is
      // determined by the renderer, so we only need within-type ordering here.
      next.sort((a, b) => a.position - b.position);
      return next;
    });
  });

  return { channels, categories, loading, setChannels, setCategories };
}

// =============================================================================
// useTypingUsers — track who's actively typing in a channel
// Server sends typing events with a 5s expiry; we evict stale entries locally too.
// =============================================================================
export function useTypingUsers(channelId: string | null) {
  const [typing, setTyping] = useState<Map<string, { username: string; expiresAt: number }>>(new Map());

  useWSEvent('typing.start', (event) => {
    if (event.channelId !== channelId) return;
    setTyping((prev) => {
      const next = new Map(prev);
      next.set(event.userId, { username: event.username, expiresAt: event.expiresAt });
      return next;
    });
  });

  // Sweep expired entries every 500ms
  useEffect(() => {
    if (!channelId) {
      setTyping(new Map());
      return;
    }
    const interval = setInterval(() => {
      setTyping((prev) => {
        const now = Date.now();
        let changed = false;
        const next = new Map(prev);
        for (const [id, info] of next) {
          if (info.expiresAt <= now) {
            next.delete(id);
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, 500);
    return () => clearInterval(interval);
  }, [channelId]);

  // Clear when switching channels
  useEffect(() => {
    setTyping(new Map());
  }, [channelId]);

  return Array.from(typing.entries()).map(([userId, info]) => ({
    userId,
    username: info.username,
  }));
}

// =============================================================================
// useServerEmojis — fetch + react to live emoji.created/deleted events
// =============================================================================
// Returns custom emoji available in the current scope.
//   - serverId set: that server's emoji only
//   - serverId null: UNION of every server the user is in (used in DMs so
//     users can use their server emoji even when there's no server scope)
// =============================================================================
export function useServerEmojis(serverId: string | null) {
  const [emojis, setEmojis] = useState<ServerEmoji[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const fetchPromise = serverId
      ? api.listServerEmojis(serverId)
      : api.listAllMyEmojis();
    fetchPromise
      .then((res) => {
        if (cancelled) return;
        // Be defensive — a stale build, a proxy stripping the response,
        // or a backend hiccup could return something unexpected. We need
        // an array no matter what or downstream `.map` calls would throw.
        const raw = Array.isArray(res?.emojis) ? res.emojis : [];
        // In all-my-emojis (DM) scope, names can collide across servers.
        // Dedupe by name — first occurrence wins. This matches what
        // FormattedText does at render time and keeps the picker clean.
        if (!serverId) {
          const seen = new Set<string>();
          const deduped: ServerEmoji[] = [];
          for (const e of raw) {
            if (seen.has(e.name)) continue;
            seen.add(e.name);
            deduped.push(e);
          }
          setEmojis(deduped);
        } else {
          setEmojis(raw);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('emoji fetch failed', err);
        setEmojis([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [serverId]);

  // WS reactivity. Behavior differs by scope:
  //   - server scope: only react to events for THIS server
  //   - all-my-emojis scope: react to events from any of the user's servers
  //     (we accept any incoming event since the gateway already broadcasts
  //     them and we'd be a member to receive them)
  useWSEvent('emoji.created', (event) => {
    if (serverId && event.serverId !== serverId) return;
    setEmojis((prev) => {
      // Avoid double-insert if our own create already ran an optimistic add.
      if (prev.some((e) => e.id === event.emoji.id)) return prev;
      return [...prev, event.emoji];
    });
  });
  useWSEvent('emoji.deleted', (event) => {
    if (serverId && event.serverId !== serverId) return;
    setEmojis((prev) => prev.filter((e) => e.id !== event.emojiId));
  });

  return { emojis, loading, setEmojis };
}

// =============================================================================
// useMembers — react to joins/leaves in real time
// =============================================================================
export function useMembers(serverId: string | null) {
  const [members, setMembers] = useState<Member[]>([]);

  useEffect(() => {
    if (!serverId) {
      setMembers([]);
      return;
    }
    let cancelled = false;
    api.listMembers(serverId).then((m) => { if (!cancelled) setMembers(m); }).catch(console.error);
    return () => { cancelled = true; };
  }, [serverId]);

  useWSEvent('member.joined', (event) => {
    if (event.serverId !== serverId) return;
    setMembers((prev) => {
      if (prev.some((m) => m.userId === event.member.userId)) return prev;
      return [...prev, event.member];
    });
  });

  useWSEvent('member.left', (event) => {
    if (event.serverId !== serverId) return;
    setMembers((prev) => prev.filter((m) => m.userId !== event.userId));
  });

  useWSEvent('member.role_changed', (event) => {
    if (event.serverId !== serverId) return;
    setMembers((prev) =>
      prev.map((m) => (m.userId === event.userId ? { ...m, role: event.role } : m))
    );
  });

  // A user updated their profile (display name or avatar). Patch any
  // member entry that matches. Fires server-wide; we don't need to filter
  // by serverId because we just match on userId — if they're not in this
  // server's member list, the map is a no-op.
  useWSEvent('user.updated', (event) => {
    setMembers((prev) =>
      prev.map((m) =>
        m.userId === event.user.id
          ? {
              ...m,
              username: event.user.username,
              displayName: event.user.displayName,
              avatarUrl: event.user.avatarUrl,
            }
          : m
      )
    );
  });

  return members;
}

// =============================================================================
// useMessages — channel history with pagination, real-time additions/edits/deletes
// =============================================================================
export function useMessages(channelId: string | null) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const myUserId = useApp((s) => s.user?.id ?? null);

  // Initial load
  useEffect(() => {
    if (!channelId) {
      setMessages([]);
      setHasMore(false);
      return;
    }
    setLoading(true);
    setError(null);
    api.listMessages(channelId, { limit: 50 })
      .then(({ messages: newMessages, hasMore: newHasMore }) => {
        // Backend returns newest-first; reverse for chronological display
        setMessages(newMessages.reverse());
        setHasMore(newHasMore);
      })
      .catch((err) => {
        setError(err instanceof ApiError ? err.message : 'Failed to load messages');
      })
      .finally(() => setLoading(false));
  }, [channelId]);

  // Load older messages (pagination)
  const loadMore = useCallback(async () => {
    if (!channelId || !hasMore || loading) return;
    const oldest = messages[0];
    if (!oldest) return;
    setLoading(true);
    try {
      const { messages: older, hasMore: more } = await api.listMessages(channelId, {
        limit: 50,
        before: oldest.id,
      });
      setMessages((prev) => [...older.reverse(), ...prev]);
      setHasMore(more);
    } finally {
      setLoading(false);
    }
  }, [channelId, hasMore, loading, messages]);

  // Real-time message events
  useWSEvent('message.created', (event) => {
    if (event.channelId !== channelId) return;
    setMessages((prev) => {
      // Dedupe by id (in case ack arrives before broadcast)
      if (prev.some((m) => m.id === event.message.id)) return prev;
      return [...prev, event.message];
    });
  });

  useWSEvent('message.updated', (event) => {
    if (event.channelId !== channelId) return;
    setMessages((prev) =>
      prev.map((m) => (m.id === event.message.id ? event.message : m))
    );
  });

  useWSEvent('message.deleted', (event) => {
    if (event.channelId !== channelId) return;
    setMessages((prev) =>
      prev.map((m) => (m.id === event.messageId ? { ...m, deleted: true, content: '' } : m))
    );
  });

  // Pin/unpin: flip the flag in place (avoids clobbering reactions/attachments
  // that a full-message replace would lose). The header pins panel fetches the
  // full list separately.
  useWSEvent('message.pinned', (event) => {
    if (event.channelId !== channelId) return;
    setMessages((prev) =>
      prev.map((m) => (m.id === event.messageId ? { ...m, pinned: true } : m))
    );
  });

  useWSEvent('message.unpinned', (event) => {
    if (event.channelId !== channelId) return;
    setMessages((prev) =>
      prev.map((m) => (m.id === event.messageId ? { ...m, pinned: false } : m))
    );
  });

  // When a user updates their avatar/display name, refresh any of their
  // messages in the loaded history. We don't refetch — just patch the
  // author block in place. Cheap and immediate.
  useWSEvent('user.updated', (event) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.author.id === event.user.id
          ? {
              ...m,
              author: {
                id: event.user.id,
                username: event.user.username,
                displayName: event.user.displayName,
                avatarUrl: event.user.avatarUrl,
              },
            }
          : m
      )
    );
  });

  // Reaction events — patch the affected message's reactions array in place.
  // Compute "mine" relative to the local user. If we're the actor, the optimistic
  // update from the click handler may already have applied — `applyReaction` is
  // idempotent so re-applying is harmless.
  useWSEvent('reaction.added', (event) => {
    if (event.channelId !== channelId) return;
    setMessages((prev) =>
      prev.map((m) =>
        m.id === event.messageId
          ? { ...m, reactions: applyReactionAdd(m.reactions ?? [], event.emoji, event.userId === myUserId) }
          : m
      )
    );
  });
  useWSEvent('reaction.removed', (event) => {
    if (event.channelId !== channelId) return;
    setMessages((prev) =>
      prev.map((m) =>
        m.id === event.messageId
          ? { ...m, reactions: applyReactionRemove(m.reactions ?? [], event.emoji, event.userId === myUserId) }
          : m
      )
    );
  });

  // Link embeds arrive asynchronously — the message itself sends fast, then
  // the backend fetches OG metadata and broadcasts an embed event a moment
  // later. We patch the relevant message's `embeds` array in place.
  useWSEvent('message.embed', (event) => {
    if (event.channelId !== channelId) return;
    setMessages((prev) =>
      prev.map((m) =>
        m.id === event.messageId
          ? { ...m, embeds: event.embeds }
          : m
      )
    );
  });

  // Toggle a reaction on a message: if I've already reacted with this emoji,
  // remove; otherwise, add. Optimistic — we apply locally first, then call
  // the API. The eventual WS broadcast is idempotent thanks to applyReaction*.
  // On API error, we revert.
  const toggleReaction = useCallback(
    async (messageId: string, emoji: string) => {
      let wasMine = false;
      setMessages((prev) => {
        return prev.map((m) => {
          if (m.id !== messageId) return m;
          const existing = (m.reactions ?? []).find((r) => r.emoji === emoji);
          if (existing?.mine) {
            wasMine = true;
            return { ...m, reactions: applyReactionRemove(m.reactions ?? [], emoji, true) };
          }
          return { ...m, reactions: applyReactionAdd(m.reactions ?? [], emoji, true) };
        });
      });
      try {
        if (wasMine) {
          await api.removeReaction(messageId, emoji);
        } else {
          await api.addReaction(messageId, emoji);
        }
      } catch {
        // Revert on failure.
        setMessages((prev) =>
          prev.map((m) => {
            if (m.id !== messageId) return m;
            return wasMine
              ? { ...m, reactions: applyReactionAdd(m.reactions ?? [], emoji, true) }
              : { ...m, reactions: applyReactionRemove(m.reactions ?? [], emoji, true) };
          })
        );
      }
    },
    []
  );

  return { messages, loading, hasMore, loadMore, error, toggleReaction };
}

// =============================================================================
// Reaction array helpers — pure functions, exported for use in optimistic
// click handlers in the message UI.
// =============================================================================

export function applyReactionAdd(
  reactions: Reaction[],
  emoji: string,
  isMine: boolean
): Reaction[] {
  const idx = reactions.findIndex((r) => r.emoji === emoji);
  if (idx === -1) {
    return [...reactions, { emoji, count: 1, mine: isMine }];
  }
  const existing = reactions[idx]!;
  // Don't double-count: if it's already mine and we're re-applying, the
  // event was a different user's add and we should still increment count
  // (mine stays true). If isMine is true and existing.mine is true, the
  // event was a duplicate of our own add — leave count alone.
  if (isMine && existing.mine) return reactions;
  const next = [...reactions];
  next[idx] = {
    ...existing,
    count: existing.count + 1,
    mine: existing.mine || isMine,
  };
  return next;
}

export function applyReactionRemove(
  reactions: Reaction[],
  emoji: string,
  isMine: boolean
): Reaction[] {
  const idx = reactions.findIndex((r) => r.emoji === emoji);
  if (idx === -1) return reactions;
  const existing = reactions[idx]!;
  // If isMine is true but existing.mine is false, the event was a duplicate
  // of our own remove that we already applied — leave alone.
  if (isMine && !existing.mine) return reactions;
  const newCount = existing.count - 1;
  if (newCount <= 0) {
    return reactions.filter((_, i) => i !== idx);
  }
  const next = [...reactions];
  next[idx] = {
    ...existing,
    count: newCount,
    mine: isMine ? false : existing.mine,
  };
  return next;
}

// =============================================================================
// useServerVoiceState — track who's in EVERY voice channel of a server
// Used by the sidebar to show participant lists under each voice channel.
// Updates live via WS events.
//
// Pass the current voice channel IDs so the hook knows which events to honor
// (vs. events for other servers).
// =============================================================================
export function useServerVoiceState(serverId: string | null, voiceChannelIds: string[] = []) {
  const [voiceState, setVoiceState] = useState<Record<string, VoiceParticipant[]>>({});
  // Stable Set we can check inside event handlers — useRef so the handler
  // closure always sees the current value without needing to re-bind.
  const channelIdsRef = useRef<Set<string>>(new Set());
  channelIdsRef.current = new Set(voiceChannelIds);

  // Sequence counter that increments on every WS-driven mutation. The REST
  // fetch records the current seq before sending and drops its response if
  // an event came in during the fetch — events are more current than a
  // pre-event snapshot.
  //
  // Without this guard, this race breaks live presence:
  //   1. User opens server A; useEffect kicks off api.serverVoiceState()
  //   2. Friend joins voice in server A
  //   3. WS event arrives, setVoiceState adds friend
  //   4. REST response (computed BEFORE step 2) arrives, overwrites state
  //      with empty — friend disappears from UI
  // The user sees friend reappear only after the next fetch trigger
  // (server-tab switch or page refresh).
  const fetchSeqRef = useRef(0);

  // Initial fetch + re-fetch when WS reconnects (covers the case where we
  // missed events while the socket was down). The 'hello' event marks every
  // successful reconnect, so we treat it as "now please re-sync."
  const refetch = useCallback(() => {
    if (!serverId) {
      setVoiceState({});
      return;
    }
    const seq = fetchSeqRef.current;
    api.serverVoiceState(serverId)
      .then((result) => {
        // Drop the response if any event-driven update happened while the
        // fetch was in flight.
        if (seq !== fetchSeqRef.current) return;
        setVoiceState(result);
      })
      .catch(() => {});
  }, [serverId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useWSEvent('hello', () => {
    refetch();
  });

  // Live updates — accept events whose channelId is in our voice channel set.
  // This handles the race where a participant joins before our initial fetch
  // returns (the channelId is in our channels list even if not yet in state).
  useWSEvent('voice.participant_joined', (event) => {
    if (!channelIdsRef.current.has(event.channelId)) return;
    fetchSeqRef.current++;
    setVoiceState((prev) => {
      const existing = prev[event.channelId] ?? [];
      if (existing.some((p) => p.userId === event.participant.userId)) return prev;
      return {
        ...prev,
        [event.channelId]: [
          ...existing,
          {
            userId: event.participant.userId,
            username: event.participant.username ?? event.participant.userId,
            displayName: event.participant.displayName ?? null,
            avatarUrl: event.participant.avatarUrl ?? null,
            joinedAt: Date.now(),
          },
        ],
      };
    });
  });

  useWSEvent('voice.participant_left', (event) => {
    if (!channelIdsRef.current.has(event.channelId)) return;
    fetchSeqRef.current++;
    setVoiceState((prev) => {
      return {
        ...prev,
        [event.channelId]: (prev[event.channelId] ?? []).filter(
          (p) => p.userId !== event.participant.userId
        ),
      };
    });
  });

  useWSEvent('voice.room_ended', (event) => {
    if (!channelIdsRef.current.has(event.channelId)) return;
    fetchSeqRef.current++;
    setVoiceState((prev) => ({ ...prev, [event.channelId]: [] }));
  });

  // Screen-share events flip the isSharing flag on a participant in-place.
  // Used by the member list to render a monitor icon next to active sharers
  // even when the viewer isn't in the same voice channel.
  useWSEvent('voice.share_started', (event) => {
    if (!channelIdsRef.current.has(event.channelId)) return;
    setVoiceState((prev) => {
      const list = prev[event.channelId];
      if (!list) return prev;
      // The participant should already be in the list (share-started always
      // follows participant_joined). If not, the next snapshot fetch will
      // reconcile — leave the state alone rather than synthesizing a
      // partial entry without metadata.
      const idx = list.findIndex((p) => p.userId === event.userId);
      if (idx < 0) return prev;
      const updated = [...list];
      updated[idx] = { ...updated[idx]!, isSharing: true };
      return { ...prev, [event.channelId]: updated };
    });
  });

  useWSEvent('voice.share_stopped', (event) => {
    if (!channelIdsRef.current.has(event.channelId)) return;
    setVoiceState((prev) => {
      const list = prev[event.channelId];
      if (!list) return prev;
      const idx = list.findIndex((p) => p.userId === event.userId);
      if (idx < 0) return prev;
      const updated = [...list];
      updated[idx] = { ...updated[idx]!, isSharing: false };
      return { ...prev, [event.channelId]: updated };
    });
  });

  return voiceState;
}

// =============================================================================
// useVoiceParticipants — track who's connected to a voice channel
// =============================================================================
export function useVoiceParticipants(channelId: string | null) {
  const [participants, setParticipants] = useState<VoiceParticipant[]>([]);

  useEffect(() => {
    if (!channelId) {
      setParticipants([]);
      return;
    }
    let cancelled = false;
    api.voiceParticipants(channelId).then((p) => { if (!cancelled) setParticipants(p); }).catch(console.error);
    return () => { cancelled = true; };
  }, [channelId]);

  useWSEvent('voice.participant_joined', (event) => {
    if (event.channelId !== channelId) return;
    setParticipants((prev) => {
      if (prev.some((p) => p.userId === event.participant.userId)) return prev;
      return [...prev, {
        userId: event.participant.userId,
        username: event.participant.username ?? event.participant.userId,
        displayName: event.participant.displayName ?? null,
        avatarUrl: event.participant.avatarUrl ?? null,
        joinedAt: Date.now(),
      }];
    });
  });

  useWSEvent('voice.participant_left', (event) => {
    if (event.channelId !== channelId) return;
    setParticipants((prev) => prev.filter((p) => p.userId !== event.participant.userId));
  });

  // Mirror the share-state flip into the single-channel view too — same
  // logic as useServerVoiceState but scoped to one channel.
  useWSEvent('voice.share_started', (event) => {
    if (event.channelId !== channelId) return;
    setParticipants((prev) => {
      const idx = prev.findIndex((p) => p.userId === event.userId);
      if (idx < 0) return prev;
      const updated = [...prev];
      updated[idx] = { ...updated[idx]!, isSharing: true };
      return updated;
    });
  });
  useWSEvent('voice.share_stopped', (event) => {
    if (event.channelId !== channelId) return;
    setParticipants((prev) => {
      const idx = prev.findIndex((p) => p.userId === event.userId);
      if (idx < 0) return prev;
      const updated = [...prev];
      updated[idx] = { ...updated[idx]!, isSharing: false };
      return updated;
    });
  });

  return participants;
}

// =============================================================================
// useVoicePresence — global "who is in any voice call right now" map
// =============================================================================
// Returns userId → channelId for every user currently connected to a voice
// room. Seeded from a one-shot snapshot (GET /voice/active) so calls already
// in progress are reflected on load, then kept live off the broadcast
// voice.participant_* / room_ended events (which every client receives). Used
// to render the "in voice" indicator on friend rows.
export function useVoicePresence(): Map<string, string> {
  const [map, setMap] = useState<Map<string, string>>(new Map());

  const refetch = useCallback(() => {
    api
      .activeVoiceUsers()
      .then((r) => setMap(new Map(Object.entries(r.active))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // Reconcile after a WS reconnect — we may have missed join/leave events.
  useWSEvent('hello', () => refetch());

  useWSEvent('voice.participant_joined', (event) => {
    setMap((prev) => {
      if (prev.get(event.participant.userId) === event.channelId) return prev;
      const next = new Map(prev);
      next.set(event.participant.userId, event.channelId);
      return next;
    });
  });

  useWSEvent('voice.participant_left', (event) => {
    setMap((prev) => {
      // Only clear if they left the room we have them in (guards against a
      // stale leave for a room they've since rejoined elsewhere).
      if (prev.get(event.participant.userId) !== event.channelId) return prev;
      const next = new Map(prev);
      next.delete(event.participant.userId);
      return next;
    });
  });

  useWSEvent('voice.room_ended', (event) => {
    setMap((prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const [uid, cid] of prev) {
        if (cid === event.channelId) {
          next.delete(uid);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  });

  return map;
}

// =============================================================================
// useDms — fetch + watch the user's DM threads
// =============================================================================
// On message.created in a DM channel we already have, we bump that thread's
// lastMessageAt and re-sort. On a message in a channel we DON'T have, we
// refetch the full list — that's how new threads (the recipient side of a
// freshly-created DM) appear without a separate dm.created event.
export function useDms() {
  const [threads, setThreads] = useState<DmThread[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const result = await api.listDms();
      setThreads(result);
    } catch (err) {
      console.error('Failed to load DMs:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useWSEvent('message.created', (event) => {
    setThreads((prev) => {
      const idx = prev.findIndex((t) => t.channelId === event.channelId);
      if (idx === -1) {
        // We don't know about this channel — could be a brand-new DM. Refetch.
        refresh();
        return prev;
      }
      const updated = [...prev];
      const t = updated[idx]!;
      updated[idx] = { ...t, lastMessageAt: event.message.createdAt };
      // Move to top
      updated.sort((a, b) => {
        const ta = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
        const tb = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
        return tb - ta;
      });
      return updated;
    });
  });

  return { threads, loading, refresh };
}

// =============================================================================
// useFriends — friends/requests/blocked list, kept live via friend.* events
// =============================================================================
export function useFriends() {
  const [data, setData] = useState<FriendsList>({
    friends: [],
    incoming: [],
    outgoing: [],
    blocked: [],
  });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setData(await api.getFriends());
    } catch (err) {
      console.error('Failed to load friends:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Any relationship change → refetch the whole snapshot (cheap, friend-group
  // scale, and avoids reconciling partial states client-side).
  useWSEvent('friend.request', () => void refresh());
  useWSEvent('friend.accepted', () => void refresh());
  useWSEvent('friend.removed', () => void refresh());

  return { ...data, loading, refresh, incomingCount: data.incoming.length };
}

// =============================================================================
// useNotifications — in-app notification feed + unread count (Profiles v2)
// =============================================================================
// Mirrors useFriends: load once, then live-update on WS events. New
// notifications are prepended optimistically; read-state changes refetch.
export function useNotifications() {
  const [notifications, setNotifications] = useState<import('./types').Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await api.getNotifications();
      setNotifications(r.notifications);
      setUnreadCount(r.unreadCount);
    } catch (err) {
      console.error('Failed to load notifications:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useWSEvent('notification.created', (e) => {
    setNotifications((prev) => [e.notification, ...prev].slice(0, 30));
    setUnreadCount((c) => c + 1);
  });
  useWSEvent('notification.read', () => void refresh());

  const markRead = useCallback(
    async (id?: string) => {
      // Optimistic: flip read-state locally, then persist.
      setNotifications((prev) => prev.map((n) => (!id || n.id === id ? { ...n, read: true } : n)));
      setUnreadCount((c) => (id ? Math.max(0, c - 1) : 0));
      try {
        await api.markNotificationsRead(id);
      } catch {
        void refresh();
      }
    },
    [refresh]
  );

  return { notifications, unreadCount, loading, refresh, markRead };
}

// =============================================================================
// useUnreads — track unread counts + mention counts per channel
// =============================================================================
// Backed by the global Zustand store so AppShell, DmSidebar, and any other
// component that needs unread state see consistent values. Without shared
// state, marking a channel as read in AppShell wouldn't update the badge
// in DmSidebar (each useState would be separate).
//
// On mount the hook does an initial /unreads fetch. It listens for
// `message.created` (bump unless I'm the author), `channel.read` (clear),
// and tab visibility changes (refetch). All mutations go through the store.
export interface UnreadState {
  unreadCount: number;
  mentionCount: number;
}

export function useUnreads() {
  const me = useApp((s) => s.user);
  const unreadByChannel = useApp((s) => s.unreadByChannel);
  const setUnreadByChannel = useApp((s) => s.setUnreadByChannel);
  const clearUnread = useApp((s) => s.clearUnread);
  const bumpUnread = useApp((s) => s.bumpUnread);

  const refresh = useCallback(async () => {
    try {
      const result = await api.listUnreads();
      // Use the latest store state at apply time so a clear that happened
      // during the fetch isn't undone by stale server data.
      const { unreadByChannel: prev, unreadClearedAt } = useApp.getState();
      const byCh: Record<string, UnreadState> = {};
      for (const entry of result.unreads) {
        const localCleared = unreadClearedAt[entry.channelId];
        const serverLastMessage = new Date(entry.lastMessageAt).getTime();
        // If we cleared this channel locally AFTER the last message the
        // server knows about, the server is just slow to reflect our read
        // (the POST hasn't propagated, or there's a clock skew issue) —
        // skip and trust local.
        if (localCleared && localCleared >= serverLastMessage) continue;
        byCh[entry.channelId] = {
          unreadCount: entry.unreadCount,
          mentionCount: entry.mentionCount,
        };
      }
      // Preserve any in-memory bumps that landed during the fetch (channels
      // present in prev but absent in the response).
      for (const [chId, state] of Object.entries(prev)) {
        if (byCh[chId]) continue;
        if (state.unreadCount > 0) {
          const localCleared = unreadClearedAt[chId];
          // Stale local clears (>60s old) don't suppress fresh unreads
          if (!localCleared || localCleared < Date.now() - 60_000) {
            byCh[chId] = state;
          }
        }
      }
      setUnreadByChannel(byCh);
    } catch (err) {
      console.error('Failed to load unreads:', err);
    }
  }, [setUnreadByChannel]);

  useEffect(() => {
    if (!me) return;
    refresh();
  }, [me, refresh]);

  // Refresh when the tab regains focus
  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === 'visible') refresh();
    }
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refresh]);

  // Bump on new messages (unless I'm the author)
  useWSEvent('message.created', (event) => {
    if (!me) return;
    if (event.message.author.id === me.id) return;
    bumpUnread(event.channelId, isMention(event.message.content, me.username));
  });

  // Clear on read receipts (from this device or another)
  useWSEvent('channel.read', (event) => {
    if (!me || event.userId !== me.id) return;
    clearUnread(event.channelId);
  });

  return {
    unreadByChannel,
    markReadLocally: clearUnread,
    refresh,
  };
}

// =============================================================================
// useAudioDevices — enumerate microphones + speakers, react to changes.
// =============================================================================
// Returns the lists of available audio inputs/outputs along with helpers
// for prompting the permission gate. The browser only returns device
// LABELS (e.g. "Blue Yeti") if the page has been granted mic permission;
// without it we get IDs but anonymous "Microphone" labels. This hook
// exposes a `requestLabels` action the settings UI can call to prompt
// once.
//
// Output device support is uneven across browsers (Safari notably lacks
// setSinkId). `outputSupported` reflects whether the current browser
// supports selecting an audio output. When false, the settings UI hides
// the output dropdown.
// =============================================================================
export interface AudioDevice {
  deviceId: string;
  label: string;
  /** Whether this device is the system default. Browsers expose a special
   *  'default' deviceId; we mark that as default. */
  isDefault: boolean;
}

export interface UseAudioDevicesResult {
  inputs: AudioDevice[];
  outputs: AudioDevice[];
  /** True when the page has mic permission, so device labels are populated.
   *  When false, devices show generic names like "Microphone 1". */
  hasLabels: boolean;
  /** False on browsers that don't support setSinkId. UI should hide the
   *  output dropdown in that case. */
  outputSupported: boolean;
  /** Trigger a one-time mic permission prompt so labels populate. */
  requestLabels: () => Promise<void>;
}

export function useAudioDevices(): UseAudioDevicesResult {
  const [inputs, setInputs] = useState<AudioDevice[]>([]);
  const [outputs, setOutputs] = useState<AudioDevice[]>([]);
  const [hasLabels, setHasLabels] = useState(false);

  // Feature-detect output device selection. setSinkId is on HTMLMediaElement.
  // We can't ask the browser "do you support this" cleanly without making a
  // dummy element; simpler to feature-test the prototype.
  const outputSupported =
    typeof window !== 'undefined' &&
    typeof HTMLAudioElement !== 'undefined' &&
    'setSinkId' in HTMLAudioElement.prototype;

  const refresh = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
      return;
    }
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const ins: AudioDevice[] = [];
      const outs: AudioDevice[] = [];
      let sawLabels = false;
      for (const d of devices) {
        if (d.kind === 'audioinput') {
          ins.push({
            deviceId: d.deviceId,
            label: d.label || `Microphone (${d.deviceId.slice(0, 8) || 'unknown'})`,
            isDefault: d.deviceId === 'default' || d.deviceId === '',
          });
          if (d.label) sawLabels = true;
        } else if (d.kind === 'audiooutput') {
          outs.push({
            deviceId: d.deviceId,
            label: d.label || `Speaker (${d.deviceId.slice(0, 8) || 'unknown'})`,
            isDefault: d.deviceId === 'default' || d.deviceId === '',
          });
          if (d.label) sawLabels = true;
        }
      }
      setInputs(ins);
      setOutputs(outs);
      setHasLabels(sawLabels);
    } catch (err) {
      console.warn('enumerateDevices failed:', err);
    }
  }, []);

  // Initial enumeration + listen for hotplug/unplug events. The browser
  // fires `devicechange` whenever the list changes (BT pair/unpair,
  // headset plug, etc).
  useEffect(() => {
    refresh();
    const md = navigator?.mediaDevices;
    if (!md) return;
    const handler = () => refresh();
    md.addEventListener('devicechange', handler);
    return () => md.removeEventListener('devicechange', handler);
  }, [refresh]);

  // Prompts mic permission once, then re-enumerates so labels populate.
  // The acquired stream is immediately released — we only wanted the
  // permission grant.
  const requestLabels = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      for (const t of stream.getTracks()) t.stop();
      await refresh();
    } catch (err) {
      console.warn('mic permission prompt rejected:', err);
    }
  }, [refresh]);

  return { inputs, outputs, hasLabels, outputSupported, requestLabels };
}

// =============================================================================
// useVideoDevices — enumerate webcams, react to hotplug, prompt for labels.
// =============================================================================
// The video sibling of useAudioDevices. Same permission caveat: the browser
// only returns camera LABELS once the page has camera permission, so we expose
// a `requestLabels` that does a one-shot getUserMedia({ video: true }) and
// re-enumerates. Reuses the AudioDevice shape (it's just {id,label,default}).
export interface UseVideoDevicesResult {
  cameras: AudioDevice[];
  hasLabels: boolean;
  requestLabels: () => Promise<void>;
}

export function useVideoDevices(): UseVideoDevicesResult {
  const [cameras, setCameras] = useState<AudioDevice[]>([]);
  const [hasLabels, setHasLabels] = useState(false);

  const refresh = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
      return;
    }
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cams: AudioDevice[] = [];
      let sawLabels = false;
      for (const d of devices) {
        if (d.kind === 'videoinput') {
          cams.push({
            deviceId: d.deviceId,
            label: d.label || `Camera (${d.deviceId.slice(0, 8) || 'unknown'})`,
            isDefault: d.deviceId === 'default' || d.deviceId === '',
          });
          if (d.label) sawLabels = true;
        }
      }
      setCameras(cams);
      setHasLabels(sawLabels);
    } catch (err) {
      console.warn('enumerateDevices (video) failed:', err);
    }
  }, []);

  useEffect(() => {
    refresh();
    const md = navigator?.mediaDevices;
    if (!md) return;
    const handler = () => refresh();
    md.addEventListener('devicechange', handler);
    return () => md.removeEventListener('devicechange', handler);
  }, [refresh]);

  const requestLabels = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      for (const t of stream.getTracks()) t.stop();
      await refresh();
    } catch (err) {
      console.warn('camera permission prompt rejected:', err);
    }
  }, [refresh]);

  return { cameras, hasLabels, requestLabels };
}

// =============================================================================
// useStandaloneMicLevel — accurate mic preview without joining a voice room.
// =============================================================================
// Returns a *fast, responsive* level meter that mirrors what the real
// noise gate processor would do if you were publishing right now.
//
// Three meter signals computed per audio frame (~60Hz):
//   - **fastLevel**: lightly-smoothed peak level. This drives both the
//     displayed bar AND the gate decision. "Lightly smoothed" = enough
//     to hide single-sample jitter, not so much that it lags speech.
//   - **displayLevel**: peak with fast attack, slow release. Shows the
//     value above; falls slowly so brief peaks register visually.
//   - **gateOpen**: matches the real NoiseGateProcessor — opens at
//     `thresholdDb`, only closes once the signal drops below
//     `thresholdDb - hysteresisDb`. Same constants as the processor.
//
// Why all three? An RMS-only smoothed meter (the previous impl) lagged
// behind voice and made the gate indicator flip half a second after
// you stopped talking, even though the real gate would have flipped
// quickly. By computing the gate against fastLevel, the indicator
// matches what your friends would actually hear.
// =============================================================================
export function useStandaloneMicLevel(opts: {
  deviceId?: string | null;
  /** Gate threshold in dB. Live-updated — pass in the user's current
   *  setting and the hook will re-evaluate without restarting capture. */
  thresholdDb?: number;
  /** Hysteresis in dB. Match the real processor (default 6). */
  hysteresisDb?: number;
} = {}): {
  /** Display level (dB), peak-with-slow-fall. Use this for the meter bar. */
  level: number;
  /** Whether the gate would currently be OPEN at the given threshold. */
  gateOpen: boolean;
  active: boolean;
  error: string | null;
  request: () => Promise<void>;
  stop: () => void;
} {
  const [level, setLevel] = useState(-100);
  const [gateOpen, setGateOpen] = useState(false);
  const [active, setActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number | null>(null);

  // Live-tunable knobs held in refs so we can update them mid-capture
  // without restarting. The audio loop reads currentRefs.thresholdDb on
  // every tick.
  const thresholdRef = useRef(opts.thresholdDb ?? -50);
  const hysteresisRef = useRef(opts.hysteresisDb ?? 6);
  useEffect(() => {
    thresholdRef.current = opts.thresholdDb ?? -50;
  }, [opts.thresholdDb]);
  useEffect(() => {
    hysteresisRef.current = opts.hysteresisDb ?? 6;
  }, [opts.hysteresisDb]);

  const stop = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (streamRef.current) {
      for (const t of streamRef.current.getTracks()) {
        try { t.stop(); } catch { /* ignore */ }
      }
      streamRef.current = null;
    }
    if (ctxRef.current) {
      ctxRef.current.close().catch(() => {});
      ctxRef.current = null;
    }
    setActive(false);
    setLevel(-100);
    setGateOpen(false);
  }, []);

  const request = useCallback(async () => {
    setError(null);
    if (streamRef.current) return;
    try {
      const constraints: MediaTrackConstraints = {};
      if (opts.deviceId) constraints.deviceId = { exact: opts.deviceId };
      const stream = await navigator.mediaDevices.getUserMedia({
        // Disable browser-side processing for the meter — we want to see
        // the raw mic. AGC would make the level look unnaturally steady;
        // browser noise-suppression would gate quiet speech below what
        // our own gate sees in the publishing pipeline.
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
          ...constraints,
        },
      });
      streamRef.current = stream;
      const ctx = new AudioContext();
      ctxRef.current = ctx;
      if (ctx.state === 'suspended') {
        await ctx.resume().catch(() => {});
      }
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      // Smaller FFT = shorter window = faster response. 512 samples at
      // 48kHz is ~10ms, plenty of resolution for level metering and
      // ~2x more responsive than the 1024 we had before.
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0;  // we do our own smoothing
      const buf = new Float32Array(analyser.fftSize);
      source.connect(analyser);
      // Note: NOT connected to ctx.destination — would cause feedback.

      // Per-frame state. fastLevel drives the gate (matches the real
      // processor exactly); displayLevel is what the bar renders.
      // Both are computed from RMS so they share a scale with the
      // threshold marker on the meter — using peak for display would
      // put the bar 6-12dB above the gate-trigger point, making it
      // look like the gate "should be open" when it isn't.
      let fastLevel = -100;
      let displayLevel = -100;
      // Local mirror of gate state so we don't read React state inside
      // the rAF loop (which would always be one frame stale).
      let gateOpenLocal = false;
      // Throttle React state updates: rAF runs ~60Hz but we only need
      // to repaint the meter every ~30Hz (33ms). Halves the render
      // pressure during preview.
      let lastEmit = 0;

      function tick() {
        analyser.getFloatTimeDomainData(buf as Float32Array<ArrayBuffer>);
        // RMS of this window. Same metric the real NoiseGateProcessor
        // uses for its gate decision — keeping the standalone preview
        // identical means what users see here is exactly what publishing
        // would do.
        let sumSq = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = buf[i] ?? 0;
          sumSq += v * v;
        }
        const rms = Math.sqrt(sumSq / buf.length);
        const rawDb = rms > 0 ? 20 * Math.log10(rms) : -100;

        // Light smoothing for the gate-input level. Just enough to filter
        // single-frame jitter; not so much it lags voice. ~50/50 means
        // we converge in ~2 frames (~33ms).
        fastLevel = fastLevel * 0.5 + rawDb * 0.5;

        // Gate decision against fastLevel using the same hysteresis as
        // the real NoiseGateProcessor. Opens at threshold; only closes
        // once we drop hysteresisDb below threshold.
        const threshold = thresholdRef.current;
        const hysteresis = hysteresisRef.current;
        if (gateOpenLocal) {
          if (fastLevel < threshold - hysteresis) gateOpenLocal = false;
        } else {
          if (fastLevel > threshold) gateOpenLocal = true;
        }

        // Display level: fast attack, slow release. Snap up immediately
        // on rising signal so the bar tracks every word; decay slowly
        // (~150ms half-life) so the bar lingers and feels lively.
        if (rawDb > displayLevel) {
          displayLevel = rawDb;            // attack: instant
        } else {
          displayLevel = displayLevel * 0.92 + rawDb * 0.08;  // release: slow
        }

        // Throttled emit to React. Both setState calls happen in the
        // same microtask so React 18 batches them — meter and gate
        // indicator update in the same paint, no one-frame lag.
        const now = performance.now();
        if (now - lastEmit >= 33) {
          lastEmit = now;
          setLevel(displayLevel);
          setGateOpen(gateOpenLocal);
        }

        rafRef.current = requestAnimationFrame(tick);
      }
      rafRef.current = requestAnimationFrame(tick);
      setActive(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to access mic');
      stop();
    }
  }, [opts.deviceId, stop]);

  // Tear down on unmount
  useEffect(() => {
    return () => stop();
  }, [stop]);
  // Restart on device change while already active
  useEffect(() => {
    if (streamRef.current) {
      stop();
      void request();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.deviceId]);

  return { level, gateOpen, active, error, request, stop };
}
